// ═══════════════════════════════════════════════════════════════
// SCHOLARSHIPS VIEW — Named Scholarship Fund Tracking
// Path: src/views/ScholarshipsView.jsx
//
// Three tabs:
//   1. Scholarships — CRUD + donor linking + award history
//   2. Awards — Record disbursements by season
//   3. Report — Donors → Scholarships → Students by season
//
// Zone Framework compliant. Gated behind can("gifts").
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { GraduationCap, Award, BarChart3 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { ScholarshipsTab } from "../components/scholarships/ScholarshipsTab";
import { AwardsTab } from "../components/scholarships/AwardsTab";
import { ScholarshipReportTab } from "../components/scholarships/ScholarshipReportTab";
import { HELP } from "../constants/helpContent";

const TABS = [
  { id: "scholarships", label: "Scholarships", icon: GraduationCap },
  { id: "awards", label: "Awards", icon: Award },
  { id: "report", label: "Report", icon: BarChart3 },
];

export const ScholarshipsView = () => {
  const { palette, goBack, goHome } = useApp();
  const [activeTab, setActiveTab] = useState("scholarships");

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Scholarships"
        subtitle="Named scholarship fund tracking"
        icon={GraduationCap}
        helpContent={HELP.scholarships}
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />

      {/* Zone 2 — Tabs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "8px 20px",
          background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
        }}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "6px 14px",
                borderRadius: 8,
                border: active ? `1px solid ${palette.accent1 || "#3b82f6"}` : `1px solid transparent`,
                background: active ? `${palette.accent1 || "#3b82f6"}12` : "transparent",
                color: active ? palette.accent1 || "#3b82f6" : palette.text2,
                fontWeight: active ? 700 : 500,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <Icon size={ICON_SIZES.sm} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 20px",
        }}
      >
        {activeTab === "scholarships" && <ScholarshipsTab />}
        {activeTab === "awards" && <AwardsTab />}
        {activeTab === "report" && <ScholarshipReportTab />}
      </div>

      <ZoneThreeFooter />
    </ViewLayout>
  );
};
